import React, { useMemo, useState } from 'react';
import { Alert, Form, Input, Modal, Select, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { EpcEstimateWbsTreeSelect } from '@/components/epc/estimation/EpcEstimateWbsTreeSelect';

export interface EpcWbsLibraryPromoteWizardProps {
  open: boolean;
  onClose: () => void;
  defaultLibraryId?: string;
  onCompleted: () => void;
}

export const EpcWbsLibraryPromoteWizard: React.FC<EpcWbsLibraryPromoteWizardProps> = ({
  open,
  onClose,
  defaultLibraryId,
  onCompleted,
}) => {
  const [form] = Form.useForm();
  const [estimateId, setEstimateId] = useState<string | undefined>();
  const [revisionId, setRevisionId] = useState<string | undefined>();

  const estimatesQ = useQuery({
    queryKey: ['epc-estimations-list'],
    queryFn: () => epcEstimationService.list(),
    enabled: open,
  });

  const workspaceQ = useQuery({
    queryKey: ['epc-est-workspace', estimateId],
    queryFn: () => epcEstimationService.getWorkspace(estimateId!),
    enabled: open && !!estimateId,
  });

  const revisions = useMemo(() => {
    const ws = workspaceQ.data as any;
    return (ws?.revisions || ws?.estimate_revisions || []) as any[];
  }, [workspaceQ.data]);

  const wbsQ = useQuery({
    queryKey: ['epc-est-wbs-promote', estimateId, revisionId],
    queryFn: () => epcEstimationService.listWbs(estimateId!, revisionId!),
    enabled: open && !!estimateId && !!revisionId,
  });

  const librariesQ = useQuery({
    queryKey: ['epc-wbs-libraries'],
    queryFn: () => epcWbsLibraryService.listLibraries(),
    enabled: open,
  });

  const promoteM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.promoteFromEstimate(String(estimateId), {
        revisionId: String(revisionId),
        libraryId: String(v.libraryId),
        packageCode: String(v.packageCode),
        packageName: String(v.packageName),
        rootWbsNodeId: String(v.rootWbsNodeId),
        includeCostLines: v.includeCostLines !== false,
      }),
    onSuccess: () => {
      message.success('Subtree promoted to library package');
      form.resetFields();
      onCompleted();
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Modal
      title="Promote estimate WBS to library package"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={promoteM.isPending}
      width={520}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Copy a WBS subtree (and optional cost lines) from an estimate revision into a reusable library package."
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ libraryId: defaultLibraryId, includeCostLines: true }}
        onFinish={(v) => promoteM.mutate(v)}
      >
        <Form.Item label="Estimate" required>
          <Select
            showSearch
            placeholder="Select estimate"
            optionFilterProp="label"
            options={(estimatesQ.data || []).map((e: any) => ({
              value: e.id,
              label: e.title || e.number || e.id,
            }))}
            onChange={(id) => {
              setEstimateId(id);
              setRevisionId(undefined);
              form.setFieldValue('rootWbsNodeId', undefined);
            }}
          />
        </Form.Item>
        <Form.Item label="Revision" required>
          <Select
            placeholder="Select revision"
            value={revisionId}
            options={revisions.map((r: any) => ({
              value: r.id,
              label: r.revision_label || r.label || r.id,
            }))}
            onChange={(id) => {
              setRevisionId(id);
              form.setFieldValue('rootWbsNodeId', undefined);
            }}
          />
        </Form.Item>
        <Form.Item name="rootWbsNodeId" label="WBS subtree root" rules={[{ required: true }]}>
          <EpcEstimateWbsTreeSelect
            nodes={(wbsQ.data || []).map((n: any) => ({
              id: String(n.id),
              code: n.code,
              name: n.name,
              parent_id: n.parent_id,
              layer_code: n.layer_code || n.wbs_layer_code,
            }))}
            disabled={!revisionId}
          />
        </Form.Item>
        <Form.Item name="libraryId" label="Target catalogue" rules={[{ required: true }]}>
          <Select
            options={(librariesQ.data || []).map((l: any) => ({
              value: l.id,
              label: `${l.code} — ${l.name}`,
            }))}
          />
        </Form.Item>
        <Space style={{ width: '100%' }} align="start">
          <Form.Item name="packageCode" label="Package code" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input placeholder="PKG-FROM-EST" />
          </Form.Item>
          <Form.Item name="packageName" label="Package name" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input placeholder="Promoted package" />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};
